import type { ReactNode } from "react";

import { AdminGoogleSignIn } from "@/components/auth/admin-google-sign-in";
import { AdminLockedPreview } from "@/components/auth/admin-locked-preview";
import { useAdminAuth } from "@/hooks/use-admin-auth";

/**
 * Locked-admin overlay tokens from design (full-bleed for any viewport):
 * - background #00000066 / rgba(0, 0, 0, 0.40)
 * - backdrop-filter blur(12.699999809265137px)
 */
const adminLockOverlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  width: "100%",
  height: "100%",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  padding: "60px",
  background: "#00000066",
  backdropFilter: "blur(12.699999809265137px)",
  WebkitBackdropFilter: "blur(12.699999809265137px)",
} as const;

export function ProtectedAdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isBootstrapping } = useAdminAuth();

  if (isAuthenticated) {
    return children;
  }

  // While restoring cookies (and when signed out), keep the locked admin shell.
  // One Tap only after bootstrap so we don't prompt a user who is already signed in.
  return (
    <div className="relative min-h-full w-full flex-1 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 select-none"
        aria-hidden
      >
        <AdminLockedPreview />
      </div>
      <div
        className="pointer-events-none z-40"
        style={adminLockOverlayStyle}
        aria-hidden
      />
      {!isBootstrapping ? <AdminGoogleSignIn overlay /> : null}
    </div>
  );
}
